import { AccountMonthlyBudget } from '../types/AccountMonthlyBudget'
import { EnhancedStatementRow } from '../../financialStatements/utils/dataEnhancement'
import { StatementDataType } from '../../financialStatements/types'
import { extractMonthlyValuesFromRegistry } from '../../../shared/utils'

interface ConvertToAccountBudgetDataOptions {
  /**
   * Grid row data with modified budget values in newBudgets registry
   */
  rowData: EnhancedStatementRow[]
}

/**
 * Converts modified grid data to AccountMonthlyBudget array for saving to backend.
 * Processes both newBudgets and newActuals registries from each row.
 *
 * @param options - Conversion options
 * @returns Array of AccountMonthlyBudget objects representing all modified budget and actuals cells
 *
 * @example
 * ```typescript
 * const budgetData = convertToAccountBudgetData({
 *   rowData: gridApi.getRenderedNodes().map(node => node.data).filter(d => d?.newBudgets || d?.newActuals)
 * })
 * // Result: [
 * //   { accountCode: 3000, year: 2024, month: 1, value: 1000, dimensionId: "123", dataType: "budget" },
 * //   { accountCode: 3000, year: 2024, month: 1, value: 950, dimensionId: "123", dataType: "actuals" }
 * // ]
 * ```
 */
export const convertToAccountBudgetData = ({ rowData }: ConvertToAccountBudgetDataOptions): AccountMonthlyBudget[] => {
  const budgetDataList: AccountMonthlyBudget[] = []

  // Process each row with changes
  rowData.forEach(row => {
    const { _sourceAccountCode, newBudgets, newActuals, dimensionId } = row

    // Skip rows without account code
    if (!_sourceAccountCode) {
      console.warn('Skipping row without _sourceAccountCode:', row)
      return
    }

    // Use dimensionId from row, or null if not set (for non-dimension rows)
    const rowDimensionId = dimensionId || null

    // Process newBudgets registry (dataType = budget)
    const budgets = extractMonthlyValuesFromRegistry(newBudgets, (year, month, value) => ({
      accountCode: _sourceAccountCode,
      year,
      month,
      value,
      dimensionId: rowDimensionId,
      dataType: StatementDataType.budget
    }))
    budgetDataList.push(...budgets)

    // Process newActuals registry (dataType = actuals)
    const actuals = extractMonthlyValuesFromRegistry(newActuals, (year, month, value) => ({
      accountCode: _sourceAccountCode,
      year,
      month,
      value,
      dimensionId: rowDimensionId,
      dataType: StatementDataType.actuals
    }))
    budgetDataList.push(...actuals)

    // NOTE: Group data (aggregated values) should NOT be saved directly.
    // Instead, they should be processed through budget allocation logic first,
    // which splits aggregated values into monthly AccountMonthlyBudget.
    // The allocation logic is handled by the BudgetAllocation feature.
  })

  return budgetDataList
}
